/* eslint-disable @typescript-eslint/no-empty-function */
import React, { FC, ReactNode, Reducer, useCallback, useMemo } from 'react'
import { AuthenticateWithGoogleResponse } from '../../services/BFF/types'

interface UIState {
  displayCartDrawer: boolean
  displaySuggestedProducts: boolean
  signUpWithGoogle: {
    show: boolean
    data: AuthenticateWithGoogleResponse | null
  }
}

const initialState = {
  displayCartDrawer: false,
  displaySuggestedProducts: false,
  signUpWithGoogle: {
    show: false,
    data: null,
  },
}

type Action =
  | { type: 'OPEN_DRAWER_CART' }
  | { type: 'CLOSE_DRAWER_CART' }
  | { type: 'OPEN_SUGGESTED_PRODUCTS' }
  | { type: 'CLOSE_SUGGESTED_PRODUCTS' }
  | { type: 'OPEN_SIGN_UP_WITH_GOOGLE'; payload: AuthenticateWithGoogleResponse }
  | { type: 'CLOSE_SIGN_UP_WITH_GOOGLE' }

const uiReducer = (state: UIState, action: Action) => {
  switch (action.type) {
    case 'OPEN_DRAWER_CART': {
      return {
        ...state,
        displayCartDrawer: true,
      }
    }
    case 'CLOSE_DRAWER_CART': {
      return {
        ...state,
        displayCartDrawer: false,
      }
    }
    case 'OPEN_SUGGESTED_PRODUCTS': {
      return {
        ...state,
        displaySuggestedProducts: true,
      }
    }
    case 'CLOSE_SUGGESTED_PRODUCTS': {
      return {
        ...state,
        displaySuggestedProducts: false,
      }
    }
    case 'OPEN_SIGN_UP_WITH_GOOGLE': {
      return {
        ...state,
        signUpWithGoogle: {
          show: true,
          data: action.payload,
        },
      }
    }
    case 'CLOSE_SIGN_UP_WITH_GOOGLE': {
      return {
        ...state,
        signUpWithGoogle: {
          ...state.signUpWithGoogle,
          show: false,
        },
      }
    }
    default: {
      return state
    }
  }
}

interface UIContextProps {
  displayCartDrawer: boolean
  displaySuggestedProducts: boolean
  signUpWithGoogle: {
    show: boolean
    data: AuthenticateWithGoogleResponse | null
  }
  openDrawerCart: () => void
  closeDrawerCart: () => void
  openSuggestedProducts: () => void
  closeSuggestedProducts: () => void
  openSignUpWithGoogle: (data: AuthenticateWithGoogleResponse) => void
  closeSignUpWithGoogle: () => void
}

export const UIContext = React.createContext<UIContextProps>({
  displayCartDrawer: initialState.displayCartDrawer,
  displaySuggestedProducts: initialState.displaySuggestedProducts,
  signUpWithGoogle: initialState.signUpWithGoogle,
  openDrawerCart: () => {},
  closeDrawerCart: () => {},
  openSuggestedProducts: () => {},
  closeSuggestedProducts: () => {},
  openSignUpWithGoogle: () => {},
  closeSignUpWithGoogle: () => {},
})

UIContext.displayName = 'UIContext'

export const UIProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const [state, dispatch] = React.useReducer<Reducer<UIState, Action>>(uiReducer, initialState)

  const openDrawerCart = useCallback(() => {
    dispatch({ type: 'OPEN_DRAWER_CART' })
  }, [])

  const closeDrawerCart = useCallback(() => {
    dispatch({ type: 'CLOSE_DRAWER_CART' })
  }, [])

  const openSuggestedProducts = useCallback(() => {
    dispatch({ type: 'OPEN_SUGGESTED_PRODUCTS' })
  }, [])

  const closeSuggestedProducts = useCallback(() => {
    dispatch({ type: 'CLOSE_SUGGESTED_PRODUCTS' })
  }, [])

  const openSignUpWithGoogle = useCallback((data: AuthenticateWithGoogleResponse) => {
    dispatch({ type: 'OPEN_SIGN_UP_WITH_GOOGLE', payload: data })
  }, [])

  const closeSignUpWithGoogle = useCallback(() => {
    dispatch({ type: 'CLOSE_SIGN_UP_WITH_GOOGLE' })
  }, [])

  const value = useMemo<UIContextProps>(
    () => ({
      ...state,
      openDrawerCart,
      closeDrawerCart,
      openSuggestedProducts,
      closeSuggestedProducts,
      openSignUpWithGoogle,
      closeSignUpWithGoogle,
    }),
    [
      closeDrawerCart,
      openDrawerCart,
      closeSuggestedProducts,
      openSuggestedProducts,
      openSignUpWithGoogle,
      closeSignUpWithGoogle,
      state,
    ],
  )

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}

export const useUI = () => {
  const context = React.useContext(UIContext)
  if (Object.values(context).some((value) => value === undefined)) {
    throw new Error('useUI must be used within a UIProvider')
  }
  return context
}
